import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { defineMessage } from 'react-intl';
import { Outlet } from 'react-router';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { ANY_SPORT_REWARDS_ESSENCE_TIER_RARITY } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { scarcityNames } from '@sorare/core/src/lib/cards';
import {
  playerPoolTierLabel,
  playerPoolTierSlug,
} from '@sorare/core/src/lib/playerPoolTiers';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import type {
  RewardsEssenceLayoutQuery,
  RewardsEssenceLayoutQueryVariables,
} from './__generated__/index.graphql.ts';
import { craftPoolTiersByRarity } from './craftPoolTiersByRarity.ts';

const REWARDS_ESSENCE_PAGE_QUERY = gql`
  query RewardsEssenceLayoutQuery($sport: Sport!) {
    currentUser {
      slug
      blockchainCardShardsChests: cardShardsChests(
        sport: $sport
        rarities: [limited, rare, super_rare, unique]
      ) {
        id
        rarity
        thresholds {
          draws {
            probabilities {
              quality
            }
            drawProbabilities {
              quality
            }
          }
        }
      }
    }
  }
` as TypedDocumentNode<
  RewardsEssenceLayoutQuery,
  RewardsEssenceLayoutQueryVariables
>;

const Root = styled.div`
  padding: var(--double-unit) 0;
`;
const Content = styled.div`
  display: grid;
  gap: var(--double-unit);

  @media ${laptopAndAbove} {
    grid-template-columns: 220px 1fr;
  }
`;

type Props = {
  tier?: string;
  rarity?: string;
};

export const RewardsEssenceLayout = ({ tier, rarity }: Props) => {
  const { sport, generateSportContextPath } = useSportContext();
  const {
    flags: {
      craftingWithGameplayTier = {
        football: false,
        nba: false,
        baseball: false,
      },
    },
  } = useFeatureFlags();
  const { data, loading } = useQuery(REWARDS_ESSENCE_PAGE_QUERY, {
    variables: {
      sport,
    },
  });

  if (loading) {
    return <LoadingIndicator />;
  }

  const { blockchainCardShardsChests } = data?.currentUser || {};

  if (!blockchainCardShardsChests) {
    return null;
  }

  const { tiers, raritiesByTierSlug } = craftPoolTiersByRarity(
    blockchainCardShardsChests,
    !!craftingWithGameplayTier[sport.toLowerCase()],
    tier
  );

  return (
    <Root>
      <PageHeader
        text={defineMessage({
          defaultMessage: 'Essence pool',
        })}
      />
      <FullWidthContainer>
        <Content>
          <div>
            <Vertical>
              {tiers.map(poolTier => {
                const slug = playerPoolTierSlug(poolTier);

                return (
                  <Link
                    key={slug}
                    to={generateSportContextPath(
                      ANY_SPORT_REWARDS_ESSENCE_TIER_RARITY,
                      {
                        params: {
                          tier: slug,
                          rarity: raritiesByTierSlug[slug][0],
                        },
                      }
                    )}
                  >
                    <BodyM
                      as="p"
                      bold={slug === tier}
                      color={
                        slug === tier ? 'var(--c-blue-400)' : 'var(--c-nd-600)'
                      }
                    >
                      {playerPoolTierLabel(poolTier)}
                    </BodyM>
                  </Link>
                );
              })}
            </Vertical>
          </div>
          <div>
            <Vertical gap={2}>
              <TabBar value={rarity} variant="flat" fullWidth>
                {!!tier &&
                  raritiesByTierSlug[tier]?.map(r => (
                    <Tab
                      key={r}
                      value={r}
                      to={generateSportContextPath(
                        ANY_SPORT_REWARDS_ESSENCE_TIER_RARITY,
                        {
                          params: {
                            tier,
                            rarity: r,
                          },
                        }
                      )}
                      replace
                    >
                      <ScarcityIcon rarity={r as Rarity} />
                      {scarcityNames[r]}
                    </Tab>
                  ))}
              </TabBar>
              <Outlet />
            </Vertical>
          </div>
        </Content>
      </FullWidthContainer>
    </Root>
  );
};
